import { useState, useCallback, SyntheticEvent, useMemo, useRef, useEffect } from 'react';
import { UseInputArgs } from './useInput';
import type { CheckboxProps as StyleGuideCheckboxProps } from '@tmapy/style-guide';

import type { OmittedFormItemProps } from '../types';

export type CheckboxProps = StyleGuideCheckboxProps<any> | OmittedFormItemProps;

export type UseCheckbox = {
  isInvalid: boolean;
  errorMessage: string;
  name?: string;
  isChecked: boolean;
  value?: number | string;
  onChange(e: SyntheticEvent): void;
  clear(): void;
};

export function useCheckbox<T extends object = {}>({
  name,
  initialValue,
  validator,
  validate,
  onChange: onChangeCallback,
  ...rest
}: T & UseInputArgs<boolean>): Omit<T, keyof UseInputArgs<boolean>> & UseCheckbox {
  const [value, setValue] = useState<boolean>(initialValue || false);
  const isInvalidRef = useRef(false);

  const onChange = useCallback(
    (e: SyntheticEvent) => {
      const newValue: boolean = (e.target as any)?.checked;

      if (!e.defaultPrevented) {
        onChangeCallback?.(e);
        if (!e.defaultPrevented) {
          setValue(newValue);
        }
      }
    },
    [onChangeCallback],
  );

  useEffect(() => {
    clear();
  }, [initialValue]);

  const clear = useCallback(() => {
    setValue(initialValue || false);
  }, [initialValue]);

  const validationResult = useMemo(() => {
    if (validate || isInvalidRef.current) {
      const errorMessage = validator?.(value);
      const isInvalid = !!errorMessage;
      isInvalidRef.current = isInvalid;

      return {
        isInvalid,
        errorMessage: errorMessage ?? '',
      };
    }
    return {
      isInvalid: false,
      errorMessage: '',
    };
  }, [value, validator, validate]);

  return {
    ...rest,
    ...validationResult,
    name,
    isChecked: value,
    value,
    onChange,
    clear,
    validator,
  } as any;
}
